const LOCAL_LINE_USER_ID_QUERY_PARAM = 'localLineUserId';
const LOCAL_LINE_USER_ID_STORAGE_KEY = 'teco_local_line_user_id';

type LocalLineSelectionParams = {
  currentUrl: string;
  storage: Storage;
  nodeEnv?: string;
  localMode?: string;
};

type LocalLineOAuthCallbackParams = Omit<
  LocalLineSelectionParams,
  'currentUrl'
> & {
  callbackUrl: string;
  state: string;
  createId?: () => string;
};

export function captureLocalLineUserId({
  currentUrl,
  storage,
  nodeEnv,
  localMode,
}: LocalLineSelectionParams): string | undefined {
  if (nodeEnv === 'production' || localMode !== 'true') return undefined;

  const url = new URL(currentUrl);
  if (!url.searchParams.has(LOCAL_LINE_USER_ID_QUERY_PARAM)) return undefined;

  const requestedLineUserId = url.searchParams
    .get(LOCAL_LINE_USER_ID_QUERY_PARAM)
    ?.trim();
  if (requestedLineUserId) {
    storage.setItem(LOCAL_LINE_USER_ID_STORAGE_KEY, requestedLineUserId);
  }
  url.searchParams.delete(LOCAL_LINE_USER_ID_QUERY_PARAM);
  return url.toString();
}

export function initializeLocalLineOAuth() {
  if (
    process.env.NODE_ENV === 'production' ||
    process.env.REACT_APP_TECO_LOCAL_MODE !== 'true'
  )
    return;

  const cleanedUrl = captureLocalLineUserId({
    currentUrl: window.location.href,
    storage: localStorage,
    nodeEnv: process.env.NODE_ENV,
    localMode: process.env.REACT_APP_TECO_LOCAL_MODE,
  });
  if (cleanedUrl) {
    window.history.replaceState(window.history.state, '', cleanedUrl);
  }
}

export function buildLocalLineOAuthCallbackUrl({
  callbackUrl,
  state,
  storage,
  nodeEnv,
  localMode,
  createId = () => `teco-local-only-${crypto.randomUUID()}`,
}: LocalLineOAuthCallbackParams): string | undefined {
  if (nodeEnv === 'production' || localMode !== 'true') return undefined;

  const storedLineUserId =
    storage.getItem(LOCAL_LINE_USER_ID_STORAGE_KEY)?.trim() || undefined;
  const lineUserId = storedLineUserId || createId();

  storage.setItem(LOCAL_LINE_USER_ID_STORAGE_KEY, lineUserId);

  const callback = new URL(callbackUrl);
  callback.searchParams.set('code', `local-line-user-${lineUserId}`);
  callback.searchParams.set('state', state);
  return callback.toString();
}
